import { useState, useEffect, useRef } from "react";
import Plot from "react-plotly.js";
import useProjectStore from "../../store/projectStore";
import Button from "../../components/ui/Button";
import useSaveShortcut from "../../hooks/useSaveShortcut";
import useBladeReadonly from "../../hooks/useBladeReadonly";
import {
  polylineFromMecoSpline,
  interpolateMecoParams,
} from "../../utils/bspline";

import { useChartColors } from "../../chartTheme";

// ── helpers ───────────────────────────────────────────────────────────────────

function resolveLeTE(line, allLines, idx) {
  const mode = line.meco_mode || "polyline";
  const p = line.meco_spline_params;

  if (mode === "bspline" && p) {
    return {
      le_r: p.le_r ?? "", le_z: p.le_z ?? "",
      te_r: p.te_r ?? "", te_z: p.te_z ?? "",
      editable: false, source: "B-Spline",
    };
  }
  if (mode === "interpolated") {
    const hubP = allLines[0]?.meco_spline_params;
    const shrP = allLines[allLines.length - 1]?.meco_spline_params;
    if (hubP && shrP) {
      const t = idx / (allLines.length - 1);
      const ip = interpolateMecoParams(hubP, shrP, t);
      return {
        le_r: ip.le_r != null ? +ip.le_r.toFixed(3) : "",
        le_z: ip.le_z != null ? +ip.le_z.toFixed(3) : "",
        te_r: ip.te_r != null ? +ip.te_r.toFixed(3) : "",
        te_z: ip.te_z != null ? +ip.te_z.toFixed(3) : "",
        editable: false, source: "Interpolated",
      };
    }
  }
  return {
    le_r: line.le_r ?? "", le_z: line.le_z ?? "",
    te_r: line.te_r ?? "", te_z: line.te_z ?? "",
    editable: true, source: null,
  };
}

function resolvePolyline(line, allLines, idx) {
  const hubP = allLines[0]?.meco_spline_params;
  const shrP = allLines[allLines.length - 1]?.meco_spline_params;
  const canInterp = hubP && shrP &&
    Object.values(hubP).every(v => v !== "" && v != null) &&
    Object.values(shrP).every(v => v !== "" && v != null);

  if (line.meco_mode === "bspline" && line.meco_spline_params)
    return polylineFromMecoSpline(line.meco_spline_params);
  if (line.meco_mode === "interpolated" && canInterp)
    return polylineFromMecoSpline(interpolateMecoParams(hubP, shrP, idx / (allLines.length - 1)));
  return { r: line.r_coords || [], z: line.z_coords || [] };
}

// ── Sub-components ────────────────────────────────────────────────────────────

function NumCell({ value, onChange, readOnly }) {
  return (
    <td className="border border-gray-200 p-0">
      <input type="number" step="any" value={value ?? ""}
        onChange={e => onChange(e.target.value === "" ? "" : parseFloat(e.target.value))}
        disabled={readOnly}
        className="w-full px-2 py-1.5 text-xs text-right bg-transparent focus:outline-none focus:bg-bh-50
          disabled:text-gray-400 disabled:bg-gray-50" />
    </td>
  );
}

// ── Interactive chart ─────────────────────────────────────────────────────────

function EdgesChart({ cls, leTe, onUpdatePoint, isLocked }) {
  const { LINE_COLORS } = useChartColors();
  const plotDivRef = useRef(null);
  const [moveMode, setMoveMode] = useState(null); // { type:"le"|"te", lineIdx, lineName }
  const [ghostPos, setGhostPos] = useState(null); // { r, z } in data coords

  // Pixel (client) → data coordinates using Plotly's internal transforms
  const pixelToData = (clientX, clientY) => {
    const gd = plotDivRef.current;
    if (!gd?._fullLayout) return null;
    const rect = gd.getBoundingClientRect();
    const { margin } = gd._fullLayout;
    const px = clientX - rect.left - margin.l;
    const py = clientY - rect.top - margin.t;
    return {
      r: +gd._fullLayout.xaxis.p2d(px).toFixed(3),
      z: +gd._fullLayout.yaxis.p2d(py).toFixed(3),
    };
  };

  // Data → pixel (relative to the container div) for ghost marker positioning
  const dataToPixel = (r, z) => {
    const gd = plotDivRef.current;
    if (!gd?._fullLayout) return null;
    const { margin } = gd._fullLayout;
    return {
      x: gd._fullLayout.xaxis.d2p(r) + margin.l,
      y: gd._fullLayout.yaxis.d2p(z) + margin.t,
    };
  };

  // Escape cancels move mode
  useEffect(() => {
    if (!moveMode) return;
    const onKey = (e) => {
      if (e.key === "Escape") { setMoveMode(null); setGhostPos(null); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [!!moveMode]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Build traces ──────────────────────────────────────────────────────────

  const traces = [];
  cls.forEach((line, i) => {
    const { r, z } = resolvePolyline(line, cls, i);
    if (!r.length) return;
    const isActive = line.active !== false;
    traces.push({
      type: "scatter", mode: "lines", name: line.name,
      x: r, y: z,
      line: { color: isActive ? LINE_COLORS[i % LINE_COLORS.length] : "#9ca3af", width: isActive ? 1.8 : 1.2 },
      opacity: isActive ? 1 : 0.4,
      showlegend: true,
    });
  });

  // Build LE/TE marker arrays, tracking which cls index each point belongs to
  const le_r = [], le_z = [], le_names = [], le_fillColors = [], le_borderColors = [], le_sizes = [], le_clsIdx = [];
  const te_r = [], te_z = [], te_names = [], te_fillColors = [], te_borderColors = [], te_sizes = [], te_clsIdx = [];

  cls.forEach((line, i) => {
    const vals = leTe[i] || resolveLeTE(line, cls, i);
    const isActive = line.active !== false;
    const base = isActive ? LINE_COLORS[i % LINE_COLORS.length] : "#9ca3af";

    if (vals.le_r !== "" && vals.le_z !== "") {
      const sel = moveMode?.type === "le" && moveMode?.lineIdx === i;
      le_r.push(+vals.le_r); le_z.push(+vals.le_z); le_names.push(line.name);
      le_fillColors.push(sel ? base + "33" : "white");
      le_borderColors.push(sel ? "#f59e0b" : base);
      le_sizes.push(sel ? 11 : 8);
      le_clsIdx.push(i);
    }
    if (vals.te_r !== "" && vals.te_z !== "") {
      const sel = moveMode?.type === "te" && moveMode?.lineIdx === i;
      te_r.push(+vals.te_r); te_z.push(+vals.te_z); te_names.push(line.name);
      te_fillColors.push(sel ? base + "33" : "white");
      te_borderColors.push(sel ? "#f59e0b" : base);
      te_sizes.push(sel ? 11 : 8);
      te_clsIdx.push(i);
    }
  });

  if (le_r.length > 1) traces.push({
    type: "scatter", mode: "lines", name: "LE edge", showlegend: false,
    x: le_r, y: le_z,
    line: { color: "#111827", width: 1 },
    hoverinfo: "skip",
  });

  if (te_r.length > 1) traces.push({
    type: "scatter", mode: "lines", name: "TE edge", showlegend: false,
    x: te_r, y: te_z,
    line: { color: "#111827", width: 1 },
    hoverinfo: "skip",
  });

  if (le_r.length) traces.push({
    type: "scatter", mode: "markers", name: "LE",
    x: le_r, y: le_z, text: le_names,
    marker: { symbol: "circle", size: le_sizes, color: le_fillColors, line: { color: le_borderColors, width: 2 } },
    hovertemplate: "%{text}<br>r=%{x:.2f}  z=%{y:.2f}<extra>LE</extra>",
  });

  if (te_r.length) traces.push({
    type: "scatter", mode: "markers", name: "TE",
    x: te_r, y: te_z, text: te_names,
    marker: { symbol: "square", size: te_sizes, color: te_fillColors, line: { color: te_borderColors, width: 2 } },
    hovertemplate: "%{text}<br>r=%{x:.2f}  z=%{y:.2f}<extra>TE</extra>",
  });

  // ── Plotly click handler ─────────────────────────────────────────────────
  const handleClick = (event) => {
    if (isLocked) return;
    const pt = event.points?.[0];
    if (!pt) return;
    if (pt.data.name !== "LE" && pt.data.name !== "TE") return;

    const type = pt.data.name === "LE" ? "le" : "te";
    const clsIdx = type === "le" ? le_clsIdx[pt.pointIndex] : te_clsIdx[pt.pointIndex];
    if (clsIdx === undefined) return;

    const resolved = resolveLeTE(cls[clsIdx], cls, clsIdx);
    if (!resolved.editable) return;

    // Toggle off if clicking the already-selected point
    if (moveMode?.type === type && moveMode?.lineIdx === clsIdx) {
      setMoveMode(null); setGhostPos(null);
    } else {
      setMoveMode({ type, lineIdx: clsIdx, lineName: cls[clsIdx].name });
      setGhostPos(null);
    }
  };

  const ghostPx = (moveMode && ghostPos) ? dataToPixel(ghostPos.r, ghostPos.z) : null;

  return (
    <div style={{ position: "relative", height: "100%" }}>
      <Plot
        data={traces}
        layout={{
          margin: { l: 55, r: 14, t: 10, b: 45 },
          xaxis: { title: "r [mm]", gridcolor: "#e5e7eb", linecolor: "#d1d5db", tickcolor: "#9ca3af", tickfont: { size: 10 }, zeroline: false, showspikes: true, spikemode: "across", spikecolor: "#9ca3af", spikethickness: 1, spikedash: "dot" },
          yaxis: { title: "z [mm]", gridcolor: "#e5e7eb", linecolor: "#d1d5db", tickcolor: "#9ca3af", tickfont: { size: 10 }, zeroline: false, showspikes: true, spikemode: "across", spikecolor: "#9ca3af", spikethickness: 1, spikedash: "dot", scaleanchor: "x", scaleratio: 1 },
          paper_bgcolor: "transparent", plot_bgcolor: "#f9fafb",
          showlegend: true,
          legend: { font: { size: 10 }, bgcolor: "rgba(255,255,255,0.88)", bordercolor: "#e5e7eb", borderwidth: 1, x: 0, xanchor: "left", y: 1, yanchor: "top" },
          hovermode: "closest",
          dragmode: "pan",
        }}
        config={{ displayModeBar: "hover", modeBarButtons: [["zoom2d", "pan2d", "autoScale2d", "resetScale2d", "toImage"]], scrollZoom: true, responsive: true, displaylogo: false }}
        style={{ width: "100%", height: "100%" }}
        useResizeHandler
        onInitialized={(_, gd) => { plotDivRef.current = gd; }}
        onUpdate={(_, gd) => { plotDivRef.current = gd; }}
        onClick={handleClick}
      />

      {/* Transparent overlay: captures placement click + mouse move for ghost */}
      {moveMode && (
        <div
          style={{ position: "absolute", inset: 0, cursor: "crosshair", zIndex: 10 }}
          onClick={(e) => {
            const pos = pixelToData(e.clientX, e.clientY);
            if (pos) onUpdatePoint(moveMode.lineIdx, moveMode.type, pos.r, pos.z);
            setMoveMode(null); setGhostPos(null);
          }}
          onMouseMove={(e) => {
            const pos = pixelToData(e.clientX, e.clientY);
            if (pos) setGhostPos(pos);
          }}
        />
      )}

      {/* Ghost marker follows cursor */}
      {ghostPx && (
        <div style={{
          position: "absolute",
          left: ghostPx.x, top: ghostPx.y,
          transform: "translate(-50%, -50%)",
          width: 14, height: 14,
          borderRadius: moveMode?.type === "le" ? "50%" : "2px",
          border: "2px dashed #f59e0b",
          background: "rgba(245,158,11,0.15)",
          pointerEvents: "none",
          zIndex: 11,
        }} />
      )}

      {/* Instruction banner */}
      {moveMode && (
        <div style={{
          position: "absolute", top: 8, left: "50%", transform: "translateX(-50%)",
          background: "#f59e0b", color: "white",
          borderRadius: 6, padding: "3px 12px",
          fontSize: 11, fontWeight: 600, zIndex: 12, pointerEvents: "none",
          whiteSpace: "nowrap", boxShadow: "0 1px 4px rgba(0,0,0,0.15)",
        }}>
          {moveMode.type.toUpperCase()} · {moveMode.lineName} — click to place · Esc to cancel
        </div>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function EdgesPage() {
  const { LINE_COLORS } = useChartColors();
  const { activeDesign, saveConstructionLines } = useProjectStore();
  const { isReadOnly } = useBladeReadonly();

  const [leTe, setLeTe] = useState({});
  const [saving, setSaving] = useState(false);
  const syncedId = useRef(null);

  useEffect(() => {
    if (!activeDesign?.id || syncedId.current === activeDesign.id) return;
    syncedId.current = activeDesign.id;

    const init = {};
    (activeDesign.construction_lines || []).forEach((cl, i) => {
      if ((cl.meco_mode || "polyline") === "polyline") {
        init[i] = { le_r: cl.le_r ?? "", le_z: cl.le_z ?? "", te_r: cl.te_r ?? "", te_z: cl.te_z ?? "" };
      }
    });
    setLeTe(init);
  }, [activeDesign]);

  const cls = activeDesign?.construction_lines || [];

  const save = async () => {
    if (!activeDesign) return;
    setSaving(true);
    try {
      const merged = cls.map((cl, i) => {
        const override = leTe[i];
        if (!override) return cl;
        return {
          ...cl,
          le_r: override.le_r !== "" ? +override.le_r : null,
          le_z: override.le_z !== "" ? +override.le_z : null,
          te_r: override.te_r !== "" ? +override.te_r : null,
          te_z: override.te_z !== "" ? +override.te_z : null,
        };
      });
      await saveConstructionLines(activeDesign.id, merged);
    } catch { /* 403 or network error — stay silent, UI already blocked */ }
    finally {
      setSaving(false);
    }
  };
  useSaveShortcut(save, { disabled: saving || isReadOnly });

  if (!activeDesign) return <div className="p-6 text-sm text-gray-500">No design loaded.</div>;

  const isLocked = isReadOnly;

  const setLeTeCell = (idx, key, val) =>
    setLeTe(prev => ({ ...prev, [idx]: { ...prev[idx], [key]: val } }));

  // Called by EdgesChart when the user drags a point to a new position
  const updatePointFromChart = (lineIdx, type, r, z) => {
    setLeTe(prev => ({
      ...prev,
      [lineIdx]: { ...prev[lineIdx], [`${type}_r`]: r, [`${type}_z`]: z },
    }));
  };

  const allLeTe = cls.map((line, i) => leTe[i] ?? resolveLeTE(line, cls, i));

  return (
    <div className="flex h-full overflow-hidden">
      {/* ── Left panel ── */}
      <div className="w-[500px] shrink-0 border-r border-gray-200 overflow-y-auto p-6 space-y-7">

        <div>
          <h2 className="text-sm font-semibold text-gray-700 mb-1">Leading Edge &amp; Trailing Edge</h2>
          <p className="text-xs text-gray-400 mb-3">
            B-Spline / interpolated lines derive LE/TE from their parametric definition.
            Click a marker on the chart to reposition it interactively.
          </p>
          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr className="bg-gray-50 text-gray-600">
                  <th className="border-b border-gray-200 px-3 py-2 text-left font-semibold">Surface</th>
                  <th className="border-b border-gray-200 px-2 py-2 font-semibold text-center" colSpan={2}>Leading Edge</th>
                  <th className="border-b border-gray-200 px-2 py-2 font-semibold text-center" colSpan={2}>Trailing Edge</th>
                  <th className="border-b border-gray-200 px-3 py-2 w-24"></th>
                </tr>
                <tr className="bg-gray-50 text-gray-500">
                  <th className="border-b border-gray-200 px-3 py-1"></th>
                  <th className="border-b border-gray-200 px-2 py-1 font-normal">r [mm]</th>
                  <th className="border-b border-gray-200 px-2 py-1 font-normal">z [mm]</th>
                  <th className="border-b border-gray-200 px-2 py-1 font-normal">r [mm]</th>
                  <th className="border-b border-gray-200 px-2 py-1 font-normal">z [mm]</th>
                  <th className="border-b border-gray-200 px-3 py-1"></th>
                </tr>
              </thead>
              <tbody>
                {cls.map((cl, i) => {
                  const resolved = resolveLeTE(cl, cls, i);
                  const vals = leTe[i] ?? resolved;
                  const isActive = cl.active !== false;
                  return (
                    <tr key={cl.name} className={`hover:bg-gray-50/50 ${!isActive ? "opacity-50" : ""}`}>
                      <td className="border-b border-gray-100 px-3 py-1">
                        <div className="flex items-center gap-1.5">
                          <span className="w-2 h-2 rounded-full shrink-0"
                            style={{ background: isActive ? LINE_COLORS[i % LINE_COLORS.length] : "#9ca3af" }} />
                          <span className="font-medium text-gray-700">{cl.name}</span>
                        </div>
                      </td>
                      <NumCell value={vals.le_r} readOnly={!resolved.editable || isLocked}
                        onChange={v => setLeTeCell(i, "le_r", v)} />
                      <NumCell value={vals.le_z} readOnly={!resolved.editable || isLocked}
                        onChange={v => setLeTeCell(i, "le_z", v)} />
                      <NumCell value={vals.te_r} readOnly={!resolved.editable || isLocked}
                        onChange={v => setLeTeCell(i, "te_r", v)} />
                      <NumCell value={vals.te_z} readOnly={!resolved.editable || isLocked}
                        onChange={v => setLeTeCell(i, "te_z", v)} />
                      <td className="border-b border-gray-100 px-3 py-1">
                        {resolved.source && (
                          <span className={`text-xs px-1.5 py-0.5 rounded
                            ${resolved.source === "B-Spline"
                              ? "bg-bh-50 text-bh-600"
                              : "bg-purple-50 text-purple-600"}`}>
                            {resolved.source}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {!isLocked && (
          <Button onClick={save} loading={saving} shortcut="Ctrl+S">Save</Button>
        )}
      </div>

      {/* ── Right panel: interactive chart ── */}
      <div className="flex-1 min-w-0 flex flex-col">
        <div className="px-4 py-3 border-b border-gray-200 shrink-0">
          <p className="text-sm font-semibold text-gray-800">Meridional View</p>
          <p className="text-xs text-gray-400">
            Construction lines · ○ Leading Edge · ▪ Trailing Edge — click a marker to reposition it
          </p>
        </div>
        <div className="flex-1 p-3">
          <EdgesChart
            cls={cls}
            leTe={allLeTe}
            onUpdatePoint={updatePointFromChart}
            isLocked={isLocked}
          />
        </div>
      </div>
    </div>
  );
}
